import axios from "axios";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import "../App.css";

import Question from "../components/Question";
import Consent from "../components/Consent";
import config from "../config";
import Allergies from "../components/Allergies";
import PersonalInfo from "../components/PersonalInfo";
import Success from "../components/Success";
import Container from "../components/Container";
import Loader from "../components/Loader";
import Addon from "../components/Addon";

const Request = () => {
  const history = useHistory();
  const { questions, index, step, apiRequest, sessionStartTime } = useSelector(
    (state) => state
  );
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  // Check if time has elapsed since session started - with periodic checking
  useEffect(() => {
    // Function to check if session time has elapsed
    const checkSessionTime = () => {
      if (sessionStartTime) {
        const now = Date.now();
        // For testing purposes, using 1 minute instead of 24 hours
        const twentyFourHoursInMs = 24 * 60 * 60 * 1000;

        if (now - sessionStartTime > twentyFourHoursInMs) {
          // Time has elapsed, reset to index 0
          console.log("Session time elapsed, redirecting to index 0");

          // Clear local storage by dispatching RESET action
          dispatch({ type: "RESET" });

          // Reset index and step
          dispatch({ type: "SET_INDEX", index: 0 });
          dispatch({ type: "SET_STEP", step: 0 });

          // Reset the session start time
          dispatch({ type: "SET_SESSION_START_TIME", sessionStartTime: now });
        }
      }
    };

    // Check immediately on mount
    checkSessionTime();

    // Set up periodic checking (every 10 seconds)
    const intervalId = setInterval(checkSessionTime, 10000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [sessionStartTime, dispatch]);

  const incrementIndex = (question, answer) => {
    let answers = apiRequest.questions;
    if (answers) {
      answers = [...answers, { question, answer }];
    } else {
      answers = [{ question, answer }];
    }
    const uniqueAnswersMap = answers.reduce((acc, answer) => {
      acc[answer.question] = answer.answer;
      return acc;
    }, {});
    answers = [];
    for (const key in uniqueAnswersMap) {
      answers.push({ question: key, answer: uniqueAnswersMap[key] });
    }

    dispatch({
      type: "SET_API_REQUEST",
      apiRequest: {
        ...apiRequest,
        questions: answers
      },
    });
    if (index + 1 === questions.length) {
      dispatch({ type: "SET_STEP", step: step + 1 });
    } else {
      dispatch({ type: "SET_INDEX", index: index + 1 });
    }
  };

  const decrementIndex = () => {
    if (index !== 0) {
      dispatch({ type: "SET_INDEX", index: index - 1 });
    }
  };

  const resetQuestion = () => {
    dispatch({ type: "SET_INDEX", index: 0 });
    dispatch({ type: "RESET_WITHOUT_QUESTIONS" });
  };

  const incrementStep = async (data) => {
    if (step === 1) {
      dispatch({
        type: "SET_API_REQUEST",
        apiRequest: {
          ...apiRequest,
          allergies: data,
        },
      });
    }
    if (step === 4) {
      const response = await axios.post(`${config.baseUrl}request/card`, {
        ...apiRequest,
        ...data,
      });
      const temp = apiRequest;
      temp.phoneNumber = temp.phoneNumber.replaceAll(/[^a-zA-Z0-9]/g, "");
      temp.identityNumber = temp.identityNumber.replaceAll(/[^a-zA-Z0-9]/g, "");
      dispatch({
        type: "SET_API_REQUEST",
        apiRequest: { ...temp, ...response.data },
      });
    }
    dispatch({ type: "SET_STEP", step: step + 1 });
  };

  const dispatchFormValues = (data) => {
    dispatch({
      type: "SET_API_REQUEST",
      apiRequest: { ...apiRequest, ...data },
    });
  };

  const decrementStep = () => {
    if (step > 0) {
      dispatch({ type: "SET_STEP", step: step - 1 });
    }
  };

  useEffect(() => {
    axios.get(`${config.baseUrl}questions`).then((res) => {
      dispatch({ type: "SET_QUESTIONS", questions: res.data });
      setLoading(false);
    });
  }, [dispatch]);

  let content;
  if (loading) {
    content = <Loader />;
  } else if (step === 0) {
    content = (
      <Question
        question={questions[index]}
        incrementIndex={incrementIndex}
        decrementIndex={decrementIndex}
        redirect={history}
        index={index}
        total={questions.length + 2}
        resetQuestion={resetQuestion}
      />
    );
  } else if (step === 1) {
    content = (
      <Allergies
        incrementStep={incrementStep}
        decrementStep={decrementStep}
        total={questions.length + 2}
        resetQuestion={resetQuestion}
        incrementIndex={incrementIndex}
        decrementIndex={decrementIndex}
        index={index + 1}
      />
    );
  } else if (step === 2) {
    content = (
      <Addon
        incrementStep={incrementStep}
        decrementStep={decrementStep}
        resetQuestion={resetQuestion}
        total={questions.length + 2}
        index={index + 2}
      />
    );
  } else if (step === 3) {
    content = (
      <Consent
        history={history}
        incrementStep={incrementStep}
        decrementStep={decrementStep}
        questions={questions}
        answers={_.uniqBy(apiRequest.questions, "question")}
        allergies={apiRequest.allergies}
      />
    );
  } else if (step === 4) {
    content = (
      <PersonalInfo
        incrementStep={incrementStep}
        decrementStep={decrementStep}
        dispatchFormValues={dispatchFormValues}
      />
    );
  } else {
    content = <Success history={history} />;
  }

  return (
    <Container>
      {content}
    </Container>
  );
};

export default Request;
